/**
 * useEnergySync Hook
 *
 * Triggers a background sync from Octopus Energy on first mount, in order:
 *   1. consumption readings + tariff agreements (parallel — independent)
 *   2. Agile rates: domestic 'N' (comparison tabs) + the meter's business
 *      product into its segregated 'N-BUS' key (parallel)
 *   3. reprice post-switch readings from the freshly-synced Agile rates
 *
 * The ordering matters: repricing must run after the rates and the agreement
 * boundary are in place, otherwise post-switch readings stay NULL until the
 * next mount. Returns a flag indicating whether new data arrived.
 */

import { useState, useEffect, useRef } from 'react'
import { supabase } from '@/supabase-client'

const FUNCTION_NAME = 'energy-sync'

export function useEnergySync() {
  const [hasNewData, setHasNewData] = useState(false)
  const syncedRef = useRef(false)

  useEffect(() => {
    if (syncedRef.current) return
    syncedRef.current = true

    const invoke = (path) => supabase.functions.invoke(`${FUNCTION_NAME}/${path}`, { method: 'POST' })

    async function sync() {
      try {
        // 1. Consumption + agreement boundary (independent of each other).
        const [consumptionRes, agreementsRes] = await Promise.all([
          invoke('sync'),
          invoke('sync-agreements'),
        ])
        if (consumptionRes.error) console.warn('[useEnergySync] Consumption sync error:', consumptionRes.error)
        if (agreementsRes.error) console.warn('[useEnergySync] Agreements sync error:', agreementsRes.error)

        // 2. Agile rates — domestic (comparison tabs) + business product (pricing).
        const [domesticRes, businessRes] = await Promise.all([
          invoke('sync-agile-rates'),
          supabase.functions.invoke(`${FUNCTION_NAME}/sync-agile-rates`, {
            method: 'POST',
            body: { meter_agile: true },
          }),
        ])
        if (domesticRes.error) console.warn('[useEnergySync] Agile (domestic) sync error:', domesticRes.error)
        if (businessRes.error) console.warn('[useEnergySync] Agile (business) sync error:', businessRes.error)

        // 3. Reprice post-switch readings now that rates + boundary are present.
        const repriceRes = await invoke('reprice-agile')
        if (repriceRes.error) console.warn('[useEnergySync] Reprice error:', repriceRes.error)

        const consumptionNew = consumptionRes.data?.data?.readings_synced > 0
        const agileNew =
          domesticRes.data?.data?.rates_synced > 0 || businessRes.data?.data?.rates_synced > 0
        const repriced = repriceRes.data?.data?.meters?.some((m) => m.priced > 0)

        if (consumptionNew || agileNew || repriced) {
          setHasNewData(true)
        }
      } catch (err) {
        console.warn('[useEnergySync] Background sync failed:', err)
      }
    }

    sync()
  }, [])

  return { hasNewData }
}

export default useEnergySync
